import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ItinerariosStateService } from './itinerarios-state.service';

@Injectable({
  providedIn: 'root'
})
export class ItinerariosNavigationService {

  constructor(
    private router: Router,
    private stateService: ItinerariosStateService
  ) { }

  /**
   * Navigate to details preserving state and POL/POD context
   */
  navigateToDetail(id: number, context?: { polName?: string; podName?: string }): void {
    const currentState = this.stateService.getCurrentState();
    const queryParams: any = this.stateService.stateToQueryParams(currentState);

    // Add POL/POD context for consistent display in detail view
    if (context?.polName) {
      queryParams.polContext = context.polName;
    }
    if (context?.podName) {
      queryParams.podContext = context.podName;
    }

    this.router.navigate(['/itinerarios', id], {
      queryParams
    });
  }

  /** Abre el detalle en una nueva pestaña (misma URL y query params que navigateToDetail). */
  openDetailInNewTab(id: number, context?: { polName?: string; podName?: string }): void {
    const currentState = this.stateService.getCurrentState();
    const queryParams: any = this.stateService.stateToQueryParams(currentState);

    if (context?.polName) {
      queryParams.polContext = context.polName;
    }
    if (context?.podName) {
      queryParams.podContext = context.podName;
    }

    const urlTree = this.router.createUrlTree(['/itinerarios', id], {
      queryParams,
    });
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Back to list preserving state
   */
  backToList(): void {
    const currentState = this.stateService.getCurrentState();
    const queryParams = this.stateService.stateToQueryParams(currentState);

    this.router.navigate(['/itinerarios'], {
      queryParams
    });
  }

  /**
   * Apply filters and update URL
   */
  applyFilters(): void {
    const currentState = this.stateService.getCurrentState();
    const queryParams = this.stateService.stateToQueryParams(currentState);

    this.router.navigate([], {
      relativeTo: this.getActivatedRoute(),
      queryParams,
      queryParamsHandling: 'replace'
    });
  }

  /**
   * Load state from URL
   */
  loadStateFromUrl(route: ActivatedRoute): boolean {
    const queryParams = route.snapshot.queryParams;

    if (this.hasValidQueryParams(queryParams)) {
      const state = this.stateService.queryParamsToState(queryParams);
      this.stateService.updateState(state);
      return true;
    }

    return false;
  }

  /**
   * Clear filters and URL
   */
  clearFilters(): void {
    this.stateService.clearState();

    this.router.navigate([], {
      relativeTo: this.getActivatedRoute(),
      queryParams: {},
      queryParamsHandling: 'replace'
    });
  }

  private hasValidQueryParams(params: any): boolean {
    const validKeys = [
      'pol', 'pod', 'carrier', 'startDate', 'endDate',
      'dateEtaFrom', 'dateEtaTo', 'page', 'limit'
    ];

    return validKeys.some(key =>
      Object.prototype.hasOwnProperty.call(params, key) &&
      params[key] !== null &&
      params[key] !== ''
    );
  }

  private getActivatedRoute(): ActivatedRoute {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }
}
